// TODO: Clean up / refactor a bit to simplify
// TODO: Document
if (!customElements.get('zoom-controls')) {
  class ZoomControls extends HTMLElement {
    constructor() {
      super();

      this.controls = [];
      this.handleMouseMove = this.onMouseMove.bind(this);
      this.handleResize = debounce(this.update.bind(this), RESIZE_DEBOUNCE_TIMER);
      this.handleZoomComplete = this.onZoomComplete.bind(this);
      this.zooming = false;
    }

    connectedCallback() {
      this.options = this.getOptions();
      this.init();

      if (this.options.observe) {
        window.addEventListener('resize', this.handleResize);
      }
    }

    disconnectedCallback() {
      if (this.options.observe) {
        window.removeEventListener('resize', this.handleResize);
      }
    }

    // TODO: Two-way bind dynamic attributes (set level / scale as attributes on update)
    init() {
      const { steps, min } = this.options;

      this.levelMin = 1;
      this.levelMax = this.levelMin + steps;
      this.level = this.levelMin;
      this.zoomScale = min;
      this.checkVisibility();
      this.setTarget();

      this.update();
    }

    // TODO: Allow setting max / step via section settings
    getDefaults() {
      return {
        min: 1,
        steps: 3,
        step: 0.5,
        panAxis: 'y',
        panBuffer: 0.2,
        panInvert: false,
        observe: true,
        duration: 0.5,
        panDuration: 0.5,
        ease: 'power1.inOut',
        panEase: 'power1.out',
      };
    }

    parseOption(option) {
      option = camelToKebab(option);

      if (!this.hasAttribute(option)) return null;
      const value = this.getAttribute(option);

      return parseAttribute(value);
    }

    getOptions() {
      const defaults = this.getDefaults();
      const options = {};
      for (const [prop, defaultValue] of Object.entries(defaults)) {
        options[prop] = this.parseOption(prop) ?? defaultValue;
      }

      return options;
    }

    setTarget() {
      this.target = document.getElementById(this.getAttribute('target'));
      this.container = this.target.parentElement;
    }

    checkVisibility() {
      this.visible = this.offsetWidth || this.offsetHeight;
    }

    isZoomed() {
      return this.level > this.levelMin;
    }

    resetPosition() {
      const { duration, ease } = this.options;
      gsap.to(this.target, { x: '', y: '', duration, ease });
    }

    calculatePanPosition(x, y) {
      const { panInvert } = this.options;

      return {
        x: this.pan.x ? this.pan.x(x) * (panInvert ? 1 : -1) : null,
        y: this.pan.y ? this.pan.y(y) * (panInvert ? 1 : -1) : null,
      };
    }

    onMouseMove(e) {
      if (this.zooming) return;

      const { x, y } = this.calculatePanPosition(e.x, e.y);

      this.panX(x);
      this.panY(y);
    }

    enablePanning() {
      const { panDuration: duration, panEase: ease } = this.options;

      this.panX = gsap.quickTo(this.target, 'x', { duration, ease });
      this.panY = gsap.quickTo(this.target, 'y', { duration, ease });

      this.panInit();

      window.addEventListener('mousemove', this.handleMouseMove);
    }

    disablePanning() {
      window.removeEventListener('mousemove', this.handleMouseMove);
      this.resetPosition();
    }

    panInit(level) {
      const zoomScale = level ? this.calculateZoom(level) : this.zoomScale;
      const { panAxis } = this.options;

      const { offsetHeight: containerHeight, offsetWidth: containerWidth } = this.container;
      const { offsetHeight: initialHeight, offsetWidth: initialWidth } = this.target;
      const targetHeight = initialHeight * zoomScale;
      const targetWidth = initialWidth * zoomScale;

      const x = (targetWidth - containerWidth) / 2;
      const y = (targetHeight - containerHeight) / 2;

      this.pan = {
        x: panAxis != 'y' && x > 0 ? this.generateTransformer(x, containerWidth) : null,
        y: panAxis != 'x' && y > 0 ? this.generateTransformer(y, containerHeight) : null,
      };
    }

    generateTransformer(edge, area) {
      const min = edge * -1;
      const max = edge;
      const start = area * this.options.panBuffer;
      const end = area * (1 - this.options.panBuffer);

      return gsap.utils.pipe(
        gsap.utils.clamp(start, end),
        gsap.utils.mapRange(start, end, min, max)
      );
    }

    reset() {
      this.zoom(this.levelMin);
    }

    setActive() {
      this.checkVisibility();
      this.active = this.visible && !!this.target;
    }

    // TODO: Test this with non-image media
    update() {
      this.setActive();
      this.updateControls();

      if (!this.active && this.isZoomed()) {
        this.reset();
        return;
      }

      if (this.level > 1) {
        this.enablePanning();
      } else {
        this.disablePanning();
      }
    }

    registerControl(control) {
      this.controls.push(control);
      this.updateControls();
    }

    deregisterControl(control) {
      this.controls.splice(this.controls.indexOf(control), 1);
      this.updateControls();
    }

    isMinZoom() {
      return this.level === this.levelMin;
    }

    isMaxZoom() {
      return this.level === this.levelMax;
    }

    outOfBounds(level) {
      return level < this.levelMin || level > this.levelMax;
    }

    updateControls() {
      this.controls.forEach(control => this.updateControl(control));
    }

    updateControl(control) {
      const disabled =
        !this.active || (control.direction === 1 ? this.isMaxZoom() : this.isMinZoom());
      control.update(!disabled);
    }

    stepZoom(direction, x, y) {
      if (!this.active) return;

      const zoomLevel = this.level + direction;

      if (this.outOfBounds(zoomLevel)) {
        console.debug(
          `Zoom ${direction > 0 ? 'in' : 'out'} failed: Zoom level ${zoomLevel} is out of bounds (${
            this.levelMin
          }-${this.levelMax})`
        );
        return;
      }

      this.zoom(zoomLevel, x, y);
    }

    zoom(level, initialX, initialY) {
      if (!level) return;

      level = gsap.utils.clamp(this.levelMin, this.levelMax, level);

      if (level === this.level) return;

      const { duration, ease } = this.options;
      const scale = this.calculateZoom(level);

      this.level = level;
      this.setZoomScale(scale);

      this.panInit();
      const { x, y } = this.calculatePanPosition(initialX, initialY);

      this.zooming = true;
      gsap.to(this.target, { scale, duration, ease, x, y, onComplete: this.handleZoomComplete });

      // TODO: Improve button state change timing when updating slides while zoomed
      this.update();
    }

    onZoomComplete() {
      this.zooming = false;
      publish(PUB_SUB_EVENTS.zoomChange, {
        data: {
          id: this.id,
          zoomLevel: {
            min: this.levelMin,
            max: this.levelMax,
            current: this.level,
            scale: this.zoomScale,
          },
          target: this.target,
        },
      });
    }

    calculateZoom(level = this.level) {
      return range(this.levelMin, level).reduce((zoom, level) => {
        if (level === 1) return zoom;

        return zoom + this.options.step * zoom;
      }, this.options.min);
    }

    setZoomScale(zoomScale) {
      this.zoomScale = zoomScale;
    }
  }

  customElements.define('zoom-controls', ZoomControls);

  class SliderZoomControls extends ZoomControls {
    constructor() {
      super();

      this.debouncedOnSlideChanged = debounce(
        this.onSlideChanged.bind(this),
        SLIDE_CHANGE_DEBOUNCE_TIMER
      );
    }

    setTarget() {
      super.setTarget();

      this.slider = this.slider ?? this.target;

      if (this.slider.initialized) {
        this.setSliderTarget();
      } else {
        this.slider.addEventListener('sliderInitialized', this.setSliderTarget.bind(this), { once: true });
      }
    }

    setSliderTarget() {
      this.target = this.slider.currentElement.querySelector('img');
    }

    onSlideChanged(e) {
      this.reset();
      this.target = e.detail.currentElement.querySelector('img');

      this.update();
    }

    connectedCallback() {
      super.connectedCallback();
      this.slider.addEventListener('slideChanged', this.debouncedOnSlideChanged);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.slider.removeEventListener('slideChanged', this.debouncedOnSlideChanged);
    }
  }

  customElements.define('slider-zoom-controls', SliderZoomControls);

  class ZoomControl extends HTMLElement {
    constructor() {
      super();

      this.button = this.querySelector('button');
      if (!this.button) return;

      this.parent = this.closest('zoom-controls, slider-zoom-controls');
      this.handleClick = this.onClick.bind(this);
      this.type = this.getType();
      this.direction = ['out', '-'].includes(this.type) ? -1 : 1;
      this.active = true;
    }

    getType() {
      return this.getAttribute('zoom') || 'in';
    }

    update(active) {
      // TODO: Skip this and update active as an attribute with 2-way binding
      this.active = active;

      if (!this.active) {
        this.button.setAttribute('disabled', 'disabled');
      } else {
        this.button.removeAttribute('disabled');
      }
    }

    connectedCallback() {
      this.button.addEventListener('click', this.handleClick);
      this.parent.registerControl(this);
    }

    disconnectedCallback() {
      this.button.removeEventListener('click', this.handleClick);
      this.parent.deregisterControl(this);
    }

    onClick(e) {
      e.preventDefault();

      this.parent.stepZoom(this.direction, e.clientX, e.clientY);
    }
  }

  customElements.define('zoom-control', ZoomControl);

  class ZoomInControl extends ZoomControl {
    constructor() {
      super();
    }

    getType() {
      return 'in';
    }
  }

  customElements.define('zoom-in-control', ZoomInControl);

  class ZoomOutControl extends ZoomControl {
    constructor() {
      super();
    }

    getType() {
      return 'out';
    }
  }

  customElements.define('zoom-out-control', ZoomOutControl);
}
